import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import pathfinder from '@fiverr-private/pathfinder';
import { makersExperience } from '@fiverr-private/logo_maker_analytics';
import { r_plus } from '@fiverr-private/icons';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Button, Icon } from '@fiverr-private/fit';
import monitor, { EVENT_TYPES } from '../../../../service/bigquery';
import { GRAPHICS_AND_DESIGN_CAT_ID, LOGO_DESIGN_SUB_CAT_ID } from './constants';
import './style.scss';

const { reportExploreDesignersClicked } = makersExperience.buyer;

const getLogoDesignRoute = () => {
    const categorySlug = translate(`categories.category_${GRAPHICS_AND_DESIGN_CAT_ID}.cached_slug`);
    const nestedSubCategorySlug = translate(`sub_categories.sub_category_${LOGO_DESIGN_SUB_CAT_ID}.cached_slug`);

    return pathfinder('short_category_sub_category', {
        category_id: categorySlug,
        id: nestedSubCategorySlug,
    });
};

const RefineResults = ({ briefId, onEditBrief }) => {
    const [sticky, setSticky] = useState(false);
    const [elevated, setElevated] = useState(false);
    const trackerRef = useRef(null);

    const handleExploreDesignersClick = () => {
        monitor.track(EVENT_TYPES.LISTINGS_EXPLORE_MARKETPLACE_CLICKED, {
            makers_project: {
                brief: { id: briefId },
            },
        });
        reportExploreDesignersClicked();
    };

    const buttonProps = {
        color: 'blue',
    };

    const logoDesignRoute = getLogoDesignRoute();

    useEffect(() => {
        const trackerContainer = trackerRef.current;
        if (!trackerContainer) {
            return;
        }

        const onIntersectionCheck = (entries) => {
            entries.forEach((entry) => {
                const intersecting = entry.intersectionRatio > 0;

                intersecting && setSticky(true);

                // The elevated is being used in order to control the 'floating' like class
                // Unlike sticky which marks when the container is initially first exposed.
                setElevated(!intersecting);
            });
        };

        const observer = new IntersectionObserver(onIntersectionCheck);

        observer.observe(trackerContainer);

        return () => observer.disconnect();
    }, []);

    const removeSticky = () => {
        setSticky(false);
        setElevated(false);
    };

    const showStickyRemoveButton = sticky && elevated;

    return (
        <>
            <div className={classnames('refine-results', { sticky, elevated })}>
                {showStickyRemoveButton && (
                    <Icon className="close" onClick={removeSticky} size={24}>
                        {r_plus}
                    </Icon>
                )}
                <h4>
                    <I18n k="logo_maker_perseus.buyer.listing.refine_results.title" />
                </h4>
                <p>
                    <I18n k="logo_maker_perseus.buyer.listing.refine_results.body" />
                </p>
                <div>
                    <Button onClick={onEditBrief} {...buttonProps}>
                        <I18n k="logo_maker_perseus.buyer.listing.refine_results.refine" />
                    </Button>
                    <Button
                        onClick={handleExploreDesignersClick}
                        href={logoDesignRoute}
                        target="_blank"
                        rel="noopener noreferrer"
                        fill="ghost"
                        {...buttonProps}
                    >
                        <I18n k="logo_maker_perseus.buyer.listing.refine_results.look_for_designer" />
                    </Button>
                </div>
            </div>
            <div className="refine-tracker" ref={trackerRef} />
        </>
    );
};

RefineResults.propTypes = {
    briefId: PropTypes.string.isRequired,
    onEditBrief: PropTypes.func,
};

export default RefineResults;
